/**
 * Profile Page
 * Displays and allows editing of user profile
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import FormInput from '../../components/auth/FormInput';
import Button from '../../components/auth/Button';
import Alert from '../../components/auth/Alert';
import { updateMe, getMe, uploadAvatar } from '../../services/authService';
import { getReferralLink, copyReferralLinkToClipboard } from '../../services/referralService';
import { coursesService, TopicInfo } from '../../services/coursesService';
import { creatorApiClient as api } from '../../services/apiClient';
import { ApiError, LanguagePreference, PhoneticSystem, DictationInputMode } from '../../types/auth';
import { languages, changeLanguage } from '../../i18n';
import { getLevelInfo, LevelInfo } from '../../services/progressionService';
import * as subscriptionService from '../../services/subscriptionService';
import type { MySubscription } from '../../services/subscriptionService';
import { useTokens } from '../../contexts/TokenContext';
import './ProfilePage.css';

/** Recording management sub-component */
const RecordingManagementSection: React.FC = () => {
  const { t } = useTranslation('profile');
  const [stats, setStats] = useState<{ totalCount: number; totalSizeBytes: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/users/me/recordings/stats');
      setStats(res.data.data);
    } catch {
      setStats({ totalCount: 0, totalSizeBytes: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete('/api/v1/users/me/recordings');
      setStats({ totalCount: 0, totalSizeBytes: 0 });
      setShowConfirm(false);
    } catch (err) {
      console.error('Failed to delete recordings:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
  };

  if (loading) {
    return <p className="recording-loading">{t('recordingManagement.loading')}</p>;
  }

  if (!stats || stats.totalCount === 0) {
    return <p className="recording-empty">{t('recordingManagement.noRecordings')}</p>;
  }

  return (
    <div className="recording-management-content">
      <div className="recording-stats">
        <span className="recording-stat">{t('recordingManagement.count', { count: stats.totalCount })}</span>
        <span className="recording-stat-separator" />
        <span className="recording-stat">{t('recordingManagement.size', { size: formatSize(stats.totalSizeBytes) })}</span>
      </div>
      {showConfirm ? (
        <div className="recording-confirm">
          <p className="recording-confirm-text">{t('recordingManagement.confirmDelete')}</p>
          <div className="recording-confirm-actions">
            <button className="profile-btn profile-btn--danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? t('recordingManagement.deleting') : t('recordingManagement.confirmYes')}
            </button>
            <button className="profile-btn" onClick={() => setShowConfirm(false)} disabled={deleting}>
              {t('recordingManagement.confirmNo')}
            </button>
          </div>
        </div>
      ) : (
        <button className="profile-btn profile-btn--danger-outline" onClick={() => setShowConfirm(true)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          {t('recordingManagement.deleteAll')}
        </button>
      )}
    </div>
  );
};

const PHONETIC_SYSTEMS = [
  { value: 'ipa', label: 'IPA' },
  { value: 'kk', label: 'KK' },
  { value: 'dj', label: 'DJ' },
  { value: 'jones', label: 'Jones' },
] as const;

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['auth', 'progression', 'profile']);
  const { user, isAuthenticated, isLoading: authLoading, updateUser } = useAuth();
  const { stats: tokenStats, isLoading: tokensLoading } = useTokens();

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile form state
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [, setProfileSuccess] = useState(false);
  const [successModal, setSuccessModal] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  // Learning preferences state
  const [languagePreference, setLanguagePreference] = useState<LanguagePreference>('zh-TW');
  const [phoneticSystem, setPhoneticSystem] = useState<PhoneticSystem>('kk');
  const [autoNaturalSpeed, setAutoNaturalSpeed] = useState(false);
  const [dictationInputMode, setDictationInputMode] = useState<DictationInputMode>('keyboard');

  // Interested topics state
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [allTopics, setAllTopics] = useState<TopicInfo[]>([]);
  const [topicChoices, setTopicChoices] = useState<Set<string>>(new Set());
  const [topicsSaving, setTopicsSaving] = useState(false);

  // Subscription details
  const [mySub, setMySub] = useState<MySubscription | null>(null);

  // Avatar upload state
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Language dropdown state
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  // Level info for mini XP display in header
  const [levelInfo, setLevelInfo] = useState<LevelInfo | null>(null);

  // Collapsible sections state (mobile accordion)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    profile: true,
    preferences: false,
  });

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Referral state
  const [referralLink, setReferralLink] = useState('');
  const [referralCopied, setReferralCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showInstagramNotice, setShowInstagramNotice] = useState(false);

  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const instagramTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/profile' } } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Close language dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFullName(user.fullName || '');
      setBio(user.bio || '');
      setAvatarUrl(user.avatarUrl || '');
      setLanguagePreference(user.languagePreference || 'zh-TW');
      setPhoneticSystem(user.phoneticSystem || 'kk');
      setAutoNaturalSpeed(user.autoNaturalSpeed || false);
      setDictationInputMode(user.dictationInputMode || 'keyboard');
    }
  }, [user]);

  // Refresh user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const freshUser = await getMe();
        updateUser(freshUser);
      } catch (error) {
        // User data will be from context
      }
    };
    if (isAuthenticated) {
      fetchUserData();
    }
  }, [isAuthenticated, updateUser]);

  // Fetch subscription details
  useEffect(() => {
    if (isAuthenticated && user?.subscriptionStatus && user.subscriptionStatus !== 'inactive') {
      subscriptionService.getMySubscription().then(setMySub).catch(() => { /* ignore */ });
    }
  }, [isAuthenticated, user?.subscriptionStatus]);

  // Fetch level info for mini XP display
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      getLevelInfo()
        .then(level => setLevelInfo(level))
        .catch(() => { /* Level info not available yet */ });
    }
  }, [isAuthenticated, user?.id]);

  // Load topic labels for display
  useEffect(() => {
    if (isAuthenticated && user?.interestedTopics?.length) {
      coursesService.getTopics()
        .then(topics => setAllTopics(topics.filter(t => t.code !== 'vocab.abstract')))
        .catch((_err: unknown) => { /* ignore */ });
    }
  }, [isAuthenticated, user?.interestedTopics]);

  // Load referral link
  useEffect(() => {
    const loadReferralLink = async () => {
      try {
        const data = await getReferralLink();
        setReferralLink(data.referralLink);
      } catch (error) {
        console.error('Failed to load referral link:', error);
      }
    };
    if (isAuthenticated) {
      loadReferralLink();
    }
  }, [isAuthenticated]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      if (instagramTimeoutRef.current) clearTimeout(instagramTimeoutRef.current);
    };
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showShareModal) {
        setShowShareModal(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showShareModal]);

  const handleCopyReferralLink = async () => {
    const success = await copyReferralLinkToClipboard(referralLink);
    if (success) {
      setReferralCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setReferralCopied(false), 2000);
    }
  };

  const shareText = t('referral.shareText');
  const shareTextEncoded = encodeURIComponent(shareText);
  const shareLinkEncoded = encodeURIComponent(referralLink);

  const socialShareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareLinkEncoded}`,
    x: `https://twitter.com/intent/tweet?url=${shareLinkEncoded}&text=${shareTextEncoded}`,
    threads: `https://www.threads.net/intent/post?text=${shareTextEncoded}%20${shareLinkEncoded}`,
    line: `https://social-plugins.line.me/lineit/share?url=${shareLinkEncoded}`,
    telegram: `https://t.me/share/url?url=${shareLinkEncoded}&text=${shareTextEncoded}`,
  };

  const handleSocialShare = (platform: keyof typeof socialShareLinks) => {
    window.open(socialShareLinks[platform], '_blank', 'noopener,noreferrer,width=600,height=400');
    setShowShareModal(false);
  };

  const handleInstagramShare = async () => {
    await handleCopyReferralLink();
    setShowInstagramNotice(true);
    if (instagramTimeoutRef.current) clearTimeout(instagramTimeoutRef.current);
    instagramTimeoutRef.current = setTimeout(() => setShowInstagramNotice(false), 4000);
    setShowShareModal(false);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(false);
    setProfileLoading(true);

    try {
      const updatedUser = await updateMe({
        fullName: fullName || undefined,
        bio: bio || undefined,
      });
      updateUser(updatedUser);
      setProfileSuccess(true);
      setSuccessModal({ show: true, message: t('profile.profileUpdated') });
    } catch (err) {
      const apiError = err as ApiError;
      setProfileError(apiError.message || t('profile.profileUpdateFailed'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAutoNaturalSpeedToggle = () => {
    const newValue = !autoNaturalSpeed;
    setAutoNaturalSpeed(newValue);
    updateMe({ autoNaturalSpeed: newValue })
      .then((updatedUser) => updateUser(updatedUser))
      .catch(() => setAutoNaturalSpeed(!newValue));
  };

  const handleDictationInputModeChange = (mode: DictationInputMode) => {
    const oldValue = dictationInputMode;
    setDictationInputMode(mode);
    localStorage.setItem('pt-dictation-input-mode', mode);
    updateMe({ dictationInputMode: mode })
      .then((updatedUser) => updateUser(updatedUser))
      .catch(() => {
        setDictationInputMode(oldValue);
        localStorage.setItem('pt-dictation-input-mode', oldValue);
      });
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setAvatarError(t('profile.avatarInvalidType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError(t('profile.avatarTooLarge'));
      return;
    }

    setAvatarError(null);
    setAvatarUploading(true);

    try {
      const result = await uploadAvatar(file);
      setAvatarUrl(result.avatarUrl);
      updateUser(result.user);
    } catch (err) {
      const apiError = err as ApiError;
      setAvatarError(apiError.message || t('profile.avatarUploadFailed'));
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getOAuthProviderName = (provider: string | undefined): string | null => {
    if (!provider) return null;
    const providerNames: Record<string, string> = { google: 'Google', github: 'GitHub' };
    return providerNames[provider.toLowerCase()] || provider;
  };

  const handleOpenTopicsModal = async () => {
    setShowTopicsModal(true);
    setTopicChoices(new Set(user?.interestedTopics || []));
    if (allTopics.length === 0) {
      try {
        const topics = await coursesService.getTopics();
        setAllTopics(topics.filter(t => t.code !== 'vocab.abstract'));
      } catch (err) {
        console.error('Failed to load topics:', err);
      }
    }
  };

  const handleTopicToggle = (code: string) => {
    setTopicChoices(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const handleSaveTopics = async () => {
    const codes = Array.from(topicChoices);
    if (codes.length < 3) return;
    setTopicsSaving(true);
    try {
      const updatedUser = await updateMe({ interestedTopics: codes });
      updateUser(updatedUser);
      setShowTopicsModal(false);
      setSuccessModal({ show: true, message: t('profile:topics.saved') });
    } catch (err) {
      console.error('Failed to save topics:', err);
    } finally {
      setTopicsSaving(false);
    }
  };

  const localizeLabel = (labels: Record<string, string>): string => {
    const loc = i18n.language;
    return labels[loc] || labels[loc.split('-')[0]] || labels['en'] || '';
  };

  if (authLoading || !user) {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="spinner" />
          <p>{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Header */}
      <div className="profile-header">
        {avatarError && (
          <Alert type="error" onDismiss={() => setAvatarError(null)}>
            {avatarError}
          </Alert>
        )}

        <div className="profile-avatar-wrapper">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleAvatarChange}
            className="profile-avatar-input"
            disabled={avatarUploading}
          />
          <button
            onClick={handleAvatarClick}
            disabled={avatarUploading}
            className="profile-avatar-btn"
            title={t('profile.uploadAvatar')}
          >
            {avatarUploading ? (
              <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
            ) : avatarUrl ? (
              <img src={avatarUrl} alt={user.username} />
            ) : (
              <span>{user.username.charAt(0).toUpperCase()}</span>
            )}
          </button>
          {!avatarUploading && (
            <div className="profile-avatar-overlay" onClick={handleAvatarClick}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          )}
        </div>

        <div className="profile-header-info">
          <h1>{fullName || user.username}</h1>
          <p className="profile-header-username">@{user.username}</p>
          <div className="profile-header-badges">
            <span className="profile-badge profile-badge--role">
              {t(`profile.role.${user.role}`)}
            </span>
            {user.emailVerified && (
              <span className="profile-badge profile-badge--verified">
                {t('profile.verified')}
              </span>
            )}
            {getOAuthProviderName(user.oauthProvider) && (
              <span className="profile-badge profile-badge--oauth">
                {t('profile.via', { provider: getOAuthProviderName(user.oauthProvider) })}
              </span>
            )}
          </div>
          {avatarUrl && user.oauthProvider && (
            <p className="profile-avatar-source">
              {t('profile.avatarFrom', { provider: getOAuthProviderName(user.oauthProvider) })}
            </p>
          )}
          <div className="profile-header-mini-row">
            {levelInfo && (
              <Link to="/progression" className="profile-xp-mini">
                <span className="profile-xp-mini-level">Lv.{levelInfo.level}</span>
                <span className="profile-xp-mini-bar">
                  <span className="profile-xp-mini-fill" style={{ width: `${levelInfo.progressPercent}%` }} />
                </span>
                <span className="profile-xp-mini-text">{levelInfo.totalXp} XP</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </Link>
            )}
            {tokenStats && (
              <Link
                to="/tokens"
                className="profile-token-mini"
                aria-label={`${t('nav:tokenBalance')}: ${tokensLoading ? '...' : tokenStats.balance.toLocaleString()}`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9 7v10h6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="profile-token-mini-amount">
                  {tokensLoading ? '...' : tokenStats.balance.toLocaleString()}
                </span>
                <span className="profile-token-mini-label">{t('nav:tokenBalance')}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="profile-sections">
          {/* Subscription Status */}
          {user && (
            <div className="profile-section profile-section--subscription">
              <h2 className="profile-section-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                {t('profile:subscription.title', '訂閱方案')}
              </h2>
              <div className="profile-subscription-card">
                <div className="profile-subscription-status">
                  <span className={`profile-sub-badge ${user.subscriptionStatus === 'active' || user.subscriptionStatus === 'trialing' ? 'active' : 'inactive'}`}>
                    {user.subscriptionStatus === 'active' && t('profile:subscription.premiumActive', 'Premium')}
                    {user.subscriptionStatus === 'trialing' && t('profile:subscription.trialing', '試用中')}
                    {user.subscriptionStatus === 'inactive' && t('profile:subscription.free', '免費方案')}
                    {user.subscriptionStatus === 'cancelled' && t('profile:subscription.cancelled', '已取消')}
                  </span>
                  {mySub?.billingPeriod && (
                    <span className="profile-sub-period">
                      {mySub.billingPeriod === 'yearly'
                        ? t('profile:subscription.yearly', '年繳')
                        : t('profile:subscription.monthly', '月繳')}
                    </span>
                  )}
                </div>
                {/* Active subscription details */}
                {user.subscriptionStatus === 'active' && mySub?.expiresAt && (
                  <div className="profile-sub-detail">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <span>
                      {t('profile:subscription.nextBilling', '下次續約：{{date}}', {
                        date: new Date(mySub.expiresAt).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })
                      })}
                    </span>
                  </div>
                )}
                {/* Trial info */}
                {user.subscriptionStatus === 'trialing' && user.trialEndsAt && (() => {
                  const trialEnd = new Date(user.trialEndsAt!);
                  const daysLeft = Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                  const dateStr = trialEnd.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' });
                  return (
                    <div className={`profile-trial-info ${daysLeft <= 3 ? 'urgent' : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span>
                        {t('profile:subscription.trialEndsOn', '試用結束日期：{{date}}', { date: dateStr })}
                        {daysLeft > 0
                          ? ` (${t('profile:subscription.daysLeft', '剩餘 {{count}} 天', { count: daysLeft })})`
                          : ` (${t('profile:subscription.endsToday', '今天到期')})`}
                      </span>
                    </div>
                  );
                })()}
                {user.subscriptionStatus === 'inactive' ? (
                  // Truly never subscribed (no Stripe customer record) — funnel
                  // them to pricing. Note: 'cancelled' users keep portal access
                  // below so they can still see past invoices + receipts.
                  <Link to="/pricing" className="profile-subscription-manage">
                    {t('profile:subscription.upgradePremium', '升級 Premium')}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </Link>
                ) : (
                  // active / trialing / cancelled — all have a Stripe customer
                  // record. Fetch a one-shot Customer Portal session and redirect
                  // for cancel / change-plan / update-card / past invoices /
                  // receipt downloads. Stripe's UI handles all of that.
                  // For cancelled users the portal still works — they just see
                  // past history rather than a "manage" action; relabel the
                  // button accordingly so the wording fits the state.
                  <button
                    type="button"
                    className="profile-subscription-manage"
                    disabled={portalLoading}
                    onClick={async () => {
                      setPortalLoading(true);
                      try {
                        const { url } = await subscriptionService.createPortalSession();
                        window.location.href = url;
                      } catch {
                        setPortalLoading(false);
                        // Falling back to pricing keeps the user un-stuck if
                        // the portal call fails (e.g. transient Stripe outage).
                        window.location.href = '/pricing';
                      }
                    }}
                  >
                    {portalLoading
                      ? t('profile:subscription.openingPortal', '開啟中…')
                      : user.subscriptionStatus === 'cancelled'
                        ? t('profile:subscription.viewBillingHistory', '查看付款紀錄')
                        : t('profile:subscription.managePlan', '管理方案')}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Profile Information */}
          <div className="profile-section">
            <button
              type="button"
              className="profile-section-header"
              onClick={() => toggleSection('profile')}
              aria-expanded={openSections.profile}
            >
              <h2 className="profile-section-title">{t('profile.profileInformation')}</h2>
              <svg className={`profile-section-chevron ${openSections.profile ? 'open' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div className={`profile-section-body ${openSections.profile ? '' : 'collapsed'}`}>
              {profileError && (
                <Alert type="error" onDismiss={() => setProfileError(null)}>
                  {profileError}
                </Alert>
              )}

              <form onSubmit={handleProfileSubmit}>
                <div className="profile-field">
                  <label className="profile-label">{t('profile.email')}</label>
                  <div className="profile-input-wrapper">
                    <input
                      type="email"
                      value={user.email}
                      disabled
                      className="profile-input-disabled has-badge"
                    />
                    {user.emailVerified ? (
                      <span className="profile-input-badge profile-input-badge--verified">
                        {t('profile.verified')}
                      </span>
                    ) : (
                      <span className="profile-input-badge profile-input-badge--unverified">
                        {t('profile.unverified')}
                      </span>
                    )}
                  </div>
                  <div className="profile-field-hint-row">
                    <span className="profile-field-hint">{t('profile.emailCannotBeChanged')}</span>
                    {!user.emailVerified && (
                      <button
                        type="button"
                        className="profile-resend-btn"
                        onClick={async () => {
                          try {
                            const { sendVerificationEmail } = await import('../../services/authService');
                            await sendVerificationEmail();
                            alert(t('profile.verificationSent'));
                          } catch (error) {
                            alert(t('profile.verificationFailed'));
                          }
                        }}
                      >
                        {t('profile.resendVerification')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="profile-field">
                  <label className="profile-label">{t('profile.username')}</label>
                  <input
                    type="text"
                    value={user.username}
                    disabled
                    className="profile-input-disabled"
                  />
                  <p className="profile-field-hint">{t('profile.usernameCannotBeChanged')}</p>
                </div>

                <FormInput
                  label={t('profile.fullName')}
                  type="text"
                  name="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t('profile.enterFullName')}
                  disabled={profileLoading}
                />

                <div className="profile-field">
                  <label className="profile-label">{t('profile.bio')}</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder={t('profile.bioPlaceholder')}
                    rows={4}
                    disabled={profileLoading}
                    className="profile-textarea"
                  />
                </div>

                <div className="profile-form-actions">
                  <Button
                    type="submit"
                    isLoading={profileLoading}
                    disabled={profileLoading}
                  >
                    {t('profile.saveChanges')}
                  </Button>
                </div>
              </form>
            </div>
          </div>

        {/* Learning Preferences */}
        <div className="profile-section">
          <button
            type="button"
            className="profile-section-header"
            onClick={() => toggleSection('preferences')}
            aria-expanded={openSections.preferences}
          >
            <h2 className="profile-section-title">{t('preferences.title')}</h2>
            <svg className={`profile-section-chevron ${openSections.preferences ? 'open' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div className={`profile-section-body ${openSections.preferences ? '' : 'collapsed'}`}>
            <div className="profile-pref-grid">
              {/* Language Preference */}
              <div className="profile-field">
                <label className="profile-label">{t('preferences.interfaceLanguage')}</label>
                <div style={{ position: 'relative' }} ref={langDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                    className="profile-lang-btn"
                  >
                    <span className="profile-lang-btn-value">
                      <span>{languages.find(l => l.code === languagePreference)?.flag}</span>
                      <span>{languages.find(l => l.code === languagePreference)?.name || languagePreference}</span>
                    </span>
                    <svg className={`profile-lang-chevron ${langDropdownOpen ? 'open' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {langDropdownOpen && (
                    <div className="profile-lang-dropdown">
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          className={`profile-lang-option ${languagePreference === lang.code ? 'active' : ''}`}
                          onClick={async () => {
                            const newValue = lang.code as LanguagePreference;
                            const oldValue = languagePreference;
                            setLanguagePreference(newValue);
                            changeLanguage(newValue);
                            setLangDropdownOpen(false);
                            try {
                              const updatedUser = await updateMe({ languagePreference: newValue });
                              updateUser(updatedUser);
                            } catch (err) {
                              setLanguagePreference(oldValue);
                              changeLanguage(oldValue);
                              console.error('Failed to save language preference:', err);
                            }
                          }}
                        >
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                          {languagePreference === lang.code && (
                            <svg className="check-icon" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="profile-field-hint">{t('preferences.interfaceLanguageHint')}</p>
              </div>

              {/* Phonetic System */}
              <div className="profile-field">
                <label className="profile-label">{t('preferences.phoneticSystem')}</label>
                <select
                  value={phoneticSystem}
                  onChange={async (e) => {
                    const newValue = e.target.value as PhoneticSystem;
                    const oldValue = phoneticSystem;
                    setPhoneticSystem(newValue);
                    try {
                      const updatedUser = await updateMe({ phoneticSystem: newValue });
                      updateUser(updatedUser);
                    } catch (err) {
                      setPhoneticSystem(oldValue);
                      console.error('Failed to save phonetic system:', err);
                    }
                  }}
                  className="profile-select"
                >
                  {PHONETIC_SYSTEMS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} - {t(`preferences.phonetic.${option.value}`)}
                    </option>
                  ))}
                </select>
                <p className="profile-field-hint">{t('preferences.phoneticSystemHint')}</p>
              </div>
            </div>

            {/* Auto Natural Speed Toggle */}
            <div className="profile-toggle-row">
              <div>
                <div className="profile-toggle-label">{t('preferences.autoNaturalSpeed')}</div>
                <p className="profile-toggle-hint">{t('preferences.autoNaturalSpeedHint')}</p>
              </div>
              <button
                type="button"
                onClick={handleAutoNaturalSpeedToggle}
                className={`profile-toggle ${autoNaturalSpeed ? 'on' : ''}`}
                role="switch"
                aria-checked={autoNaturalSpeed}
              >
                <span className="profile-toggle-knob" />
              </button>
            </div>

            {/* Dictation Input Mode */}
            <div className="profile-field" style={{ marginTop: 16 }}>
              <label className="profile-label">{t('preferences.dictationInputMode')}</label>
              <div className="profile-input-mode-options">
                <button
                  type="button"
                  className={`profile-input-mode-btn ${dictationInputMode === 'keyboard' ? 'active' : ''}`}
                  onClick={() => handleDictationInputModeChange('keyboard')}
                >
                  <svg className="profile-input-mode-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="6" width="20" height="12" rx="2" />
                    <line x1="6" y1="10" x2="6" y2="10" /><line x1="10" y1="10" x2="10" y2="10" /><line x1="14" y1="10" x2="14" y2="10" /><line x1="18" y1="10" x2="18" y2="10" />
                    <line x1="8" y1="14" x2="16" y2="14" />
                  </svg>
                  <span>{t('preferences.dictationModes.keyboard')}</span>
                </button>
                <button
                  type="button"
                  className={`profile-input-mode-btn ${dictationInputMode === 'letter-picker' ? 'active' : ''}`}
                  onClick={() => handleDictationInputModeChange('letter-picker')}
                >
                  <svg className="profile-input-mode-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="7" height="7" rx="1.5" /><rect x="14" y="4" width="7" height="7" rx="1.5" />
                    <rect x="3" y="13" width="7" height="7" rx="1.5" /><rect x="14" y="13" width="7" height="7" rx="1.5" />
                    <text x="6.5" y="9.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">a</text>
                    <text x="17.5" y="9.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">b</text>
                    <text x="6.5" y="18.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">c</text>
                    <text x="17.5" y="18.5" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">d</text>
                  </svg>
                  <span>{t('preferences.dictationModes.letterPicker')}</span>
                </button>
              </div>
              <p className="profile-field-hint">{t('preferences.dictationInputModeHint')}</p>
            </div>

            {/* Interested Topics */}
            <div className="profile-topics-row">
              <div className="profile-topics-header">
                <div>
                  <div className="profile-toggle-label">{t('profile:topics.title')}</div>
                  <p className="profile-toggle-hint">{t('profile:topics.hint')}</p>
                </div>
                <button
                  type="button"
                  className="profile-topics-edit-btn"
                  onClick={handleOpenTopicsModal}
                >
                  {t('profile:topics.edit')}
                </button>
              </div>
              {user.interestedTopics && user.interestedTopics.length > 0 ? (
                <div className="profile-topics-chips">
                  {user.interestedTopics.map(code => {
                    const topic = allTopics.find(t => t.code === code);
                    return (
                      <span key={code} className="profile-topic-chip">
                        {topic?.icon && <span>{topic.icon}</span>}
                        {topic ? localizeLabel(topic.labels) : code}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="profile-topics-empty">{t('profile:topics.empty')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Recording Management */}
        <div className="profile-section profile-section--recordings">
          <h2 className="profile-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
            {t('profile:recordingManagement.title')}
          </h2>
          <RecordingManagementSection />
        </div>

        {/* Referral — compact */}
        <div className="profile-section profile-section--referral">
          <div className="profile-referral-compact">
            <div className="profile-referral-left">
              <h2 className="profile-section-title">{t('referral.title')}</h2>
              <p className="profile-referral-desc">{t('referral.description')}</p>
            </div>
            <Link to="/referral" className="profile-referral-link">
              {t('referral.viewDetails')} &rarr;
            </Link>
          </div>
          <div className="profile-referral-actions">
            <input
              type="text"
              value={referralLink}
              readOnly
              aria-label={t('referral.referralLinkLabel')}
              className="profile-referral-input"
              placeholder={t('referral.loading')}
            />
            <button
              onClick={handleCopyReferralLink}
              disabled={!referralLink}
              aria-label={referralCopied ? t('referral.copiedAriaLabel') : t('referral.copyLinkAriaLabel')}
              className={`profile-btn profile-btn--copy ${referralCopied ? 'copied' : ''}`}
            >
              {referralCopied ? (
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => setShowShareModal(true)}
              disabled={!referralLink}
              aria-label={t('referral.shareWithFriends')}
              className="profile-btn profile-btn--share"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Instagram Notice Toast */}
      {showInstagramNotice && (
        <div className="profile-toast" role="alert" aria-live="polite">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>{t('referral.instagramNotice')}</span>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="profile-modal-overlay" onClick={() => setShowShareModal(false)}
          role="dialog" aria-modal="true" aria-labelledby="share-modal-title">
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h3 id="share-modal-title" className="profile-modal-title">{t('referral.shareModalTitle')}</h3>
              <button onClick={() => setShowShareModal(false)} className="profile-modal-close"
                aria-label={t('referral.closeShareModal')}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="profile-share-grid">
              <button onClick={() => handleSocialShare('facebook')} className="profile-share-btn"
                aria-label={t('referral.shareTo.facebook')}>
                <div className="profile-share-icon" style={{ background: '#1877F2' }}>
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span>Facebook</span>
              </button>

              <button onClick={handleInstagramShare} className="profile-share-btn"
                aria-label={t('referral.shareTo.instagram')}>
                <div className="profile-share-icon" style={{ background: '#E1306C' }}>
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <span>Instagram</span>
              </button>

              <button onClick={() => handleSocialShare('x')} className="profile-share-btn"
                aria-label={t('referral.shareTo.x')}>
                <div className="profile-share-icon" style={{ background: '#000' }}>
                  <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: 22, height: 22 }}>
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <span>X</span>
              </button>

              <button onClick={() => handleSocialShare('threads')} className="profile-share-btn"
                aria-label={t('referral.shareTo.threads')}>
                <div className="profile-share-icon" style={{ background: '#000' }}>
                  <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: 22, height: 22 }}>
                    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.812-.674 1.894-1.073 3.119-1.153 1.048-.07 2.036.07 2.943.392l.015-1.503c0-.637-.025-1.143-.074-1.597-.264-2.453-1.617-3.705-4.022-3.724-1.467.012-2.66.473-3.546 1.372l-1.402-1.438C7.496 2.283 9.333 1.573 11.573 1.5h.056c1.647.009 3.065.378 4.216 1.096 1.152.718 2.007 1.73 2.543 3.01.392.936.596 2.013.607 3.201.005.566.007 1.138.005 1.713l-.002.466c.964.473 1.783 1.133 2.401 1.944.843 1.108 1.208 2.466 1.086 4.037-.152 1.97-.978 3.608-2.456 4.875-1.61 1.38-3.873 2.123-6.546 2.148zm-.129-8.847c-1.035.07-1.9.36-2.434.82-.477.41-.681.898-.646 1.538.034.606.303 1.097.757 1.382.56.352 1.325.487 2.152.444 1.03-.058 1.87-.464 2.428-1.178.405-.517.693-1.2.839-1.989-.889-.283-1.96-.464-3.096-.464z"/>
                  </svg>
                </div>
                <span>Threads</span>
              </button>

              <button onClick={() => handleSocialShare('line')} className="profile-share-btn"
                aria-label={t('referral.shareTo.line')}>
                <div className="profile-share-icon" style={{ background: '#00B900' }}>
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.105.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                  </svg>
                </div>
                <span>LINE</span>
              </button>

              <button onClick={() => handleSocialShare('telegram')} className="profile-share-btn"
                aria-label={t('referral.shareTo.telegram')}>
                <div className="profile-share-icon" style={{ background: '#0088CC' }}>
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </div>
                <span>Telegram</span>
              </button>
            </div>

            <div className="profile-share-divider">
              <p>{t('referral.orCopyLinkManually')}</p>
              <div className="profile-share-copy-row">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  aria-label={t('referral.referralLinkLabel')}
                  className="profile-share-copy-input"
                />
                <button
                  onClick={handleCopyReferralLink}
                  className={`profile-share-copy-btn ${referralCopied ? 'copied' : ''}`}
                >
                  {referralCopied ? t('referral.copied') : t('referral.copy')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Topics Edit Modal */}
      {showTopicsModal && (
        <div className="profile-modal-overlay" onClick={() => setShowTopicsModal(false)}>
          <div className="profile-modal profile-topics-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-modal-header">
              <h3 className="profile-modal-title">{t('profile:topics.modalTitle')}</h3>
              <button onClick={() => setShowTopicsModal(false)} className="profile-modal-close">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="profile-topics-modal-hint">{t('profile:topics.modalHint')}</p>
            <div className="profile-topics-grid">
              {allTopics.map(topic => {
                const selected = topicChoices.has(topic.code);
                return (
                  <button
                    key={topic.code}
                    className={`profile-topic-option${selected ? ' selected' : ''}`}
                    onClick={() => handleTopicToggle(topic.code)}
                  >
                    {topic.icon && <span className="profile-topic-option-icon">{topic.icon}</span>}
                    <span>{localizeLabel(topic.labels)}</span>
                    {topic.courseCount > 0 && (
                      <span className="profile-topic-option-count">{topic.courseCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="profile-topics-modal-footer">
              <span className="profile-topics-count">
                {t('profile:topics.selected', { count: topicChoices.size })}
              </span>
              <button
                className="profile-topics-save-btn"
                onClick={handleSaveTopics}
                disabled={topicChoices.size < 3 || topicsSaving}
              >
                {topicsSaving ? t('profile:topics.saving') : t('profile:topics.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {successModal.show && (
        <div className="profile-modal-overlay" onClick={() => setSuccessModal({ show: false, message: '' })}
          role="dialog" aria-modal="true">
          <div className="profile-modal profile-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-success-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="profile-success-msg">{successModal.message}</p>
            <button
              onClick={() => setSuccessModal({ show: false, message: '' })}
              className="profile-success-confirm"
            >
              {t('common:confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
